// Register Page - Modern Design with Security Features
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

export default function Register() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const validateField = (name: string, value: string) => {
    switch (name) {
      case 'firstName':
        if (!value.trim()) return 'First name is required';
        if (value.length < 2) return 'First name must be at least 2 characters';
        return '';
      case 'lastName':
        if (!value.trim()) return 'Last name is required';
        if (value.length < 2) return 'Last name must be at least 2 characters';
        return '';
      case 'email':
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value) return 'Email is required';
        if (!emailRe.test(value)) return 'Please enter a valid email address';
        return '';
      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
          return 'Password must contain uppercase, lowercase, and number';
        }
        return '';
      case 'confirmPassword':
        if (value !== formData.password) return 'Passwords do not match';
        return '';
      case 'phone':
        if (value && !/^[0-9+\s-]{10,}$/.test(value)) {
          return 'Please enter a valid phone number';
        }
        return '';
      default:
        return '';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors: Record<string, string> = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key as keyof typeof formData]);
      if (error) newErrors[key] = error;
    });

    if (!acceptTerms) {
      newErrors.terms = 'You must accept the terms and conditions';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    try {
      await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || undefined
      });
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    const { password } = formData;
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/(?=.*[a-z])/.test(password)) strength++;
    if (/(?=.*[A-Z])/.test(password)) strength++;
    if (/(?=.*\d)/.test(password)) strength++;
    if (/(?=.*[!@#$%^&*])/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength();
  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500', 'bg-green-600'];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-base-100 via-pink-950/20 to-amber-950/20 p-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-pink-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      </div>
      <div className="card bg-base-100/80 backdrop-blur-xl shadow-2xl w-full max-w-lg relative z-10 border border-white/10">
        <div className="card-body p-8">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center justify-center gap-3 mb-6 group">
              <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-amber-400 rounded-2xl flex items-center justify-center shadow-glow-gold group-hover:scale-105 transition-transform duration-300">
                <span className="text-white font-black text-2xl">EG</span>
              </div>
            </Link>
            <h1 className="font-heading text-3xl font-bold bg-gradient-to-r from-white via-pink-200 to-amber-200 bg-clip-text text-transparent">
              Create Account
            </h1>
            <p className="text-gray-400 mt-2">Join Elite Gymnastics today</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="form-control">
                <label className="label"><span className="label-text text-gray-300 font-medium">First Name</span></label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`input input-bordered w-full bg-base-200/50 border-white/10 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 ${errors.firstName ? 'input-error' : ''}`}
                  placeholder="John"
                  disabled={loading}
                  autoComplete="given-name"
                />
                {errors.firstName && <label className="label"><span className="label-text-alt text-error">{errors.firstName}</span></label>}
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text text-gray-300 font-medium">Last Name</span></label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`input input-bordered w-full bg-base-200/50 border-white/10 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 ${errors.lastName ? 'input-error' : ''}`}
                  placeholder="Doe"
                  disabled={loading}
                  autoComplete="family-name"
                />
                {errors.lastName && <label className="label"><span className="label-text-alt text-error">{errors.lastName}</span></label>}
              </div>
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text text-gray-300 font-medium">Email Address</span></label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`input input-bordered w-full bg-base-200/50 border-white/10 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 ${errors.email ? 'input-error' : ''}`}
                placeholder="your@email.com"
                disabled={loading}
                autoComplete="email"
              />
              {errors.email && <label className="label"><span className="label-text-alt text-error">{errors.email}</span></label>}
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text text-gray-300 font-medium">Phone (Optional)</span></label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`input input-bordered w-full bg-base-200/50 border-white/10 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 ${errors.phone ? 'input-error' : ''}`}
                placeholder="+44 123 456 7890"
                disabled={loading}
                autoComplete="tel"
              />
              {errors.phone && <label className="label"><span className="label-text-alt text-error">{errors.phone}</span></label>}
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text text-gray-300 font-medium">Password</span></label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`input input-bordered w-full pr-12 bg-base-200/50 border-white/10 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 ${errors.password ? 'input-error' : ''}`}
                  placeholder="Create a strong password"
                  disabled={loading}
                  autoComplete="new-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors">
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {formData.password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className={`h-1 flex-1 rounded-full ${i < passwordStrength ? strengthColors[passwordStrength - 1] : 'bg-gray-700'}`} />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">
                    {passwordStrength <= 2 ? 'Weak' : passwordStrength <= 3 ? 'Medium' : passwordStrength <= 4 ? 'Strong' : 'Very Strong'}
                  </p>
                </div>
              )}
              {errors.password && <label className="label"><span className="label-text-alt text-error">{errors.password}</span></label>}
            </div>
            <div className="form-control">
              <label className="label"><span className="label-text text-gray-300 font-medium">Confirm Password</span></label>
              <input
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`input input-bordered w-full bg-base-200/50 border-white/10 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 ${errors.confirmPassword ? 'input-error' : ''}`}
                placeholder="Confirm your password"
                disabled={loading}
                autoComplete="new-password"
              />
              {errors.confirmPassword && <label className="label"><span className="label-text-alt text-error">{errors.confirmPassword}</span></label>}
            </div>
            <div className="form-control">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={e => { setAcceptTerms(e.target.checked); setErrors(prev => ({ ...prev, terms: '' })); }}
                  className="checkbox checkbox-primary"
                />
                <span className="text-sm text-gray-400">
                  I agree to the <Link to="/terms" className="text-pink-400 hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-pink-400 hover:underline">Privacy Policy</Link>
                </span>
              </label>
              {errors.terms && <label className="label"><span className="label-text-alt text-error">{errors.terms}</span></label>}
            </div>
            <button
              type="submit"
              className={`btn w-full h-12 bg-gradient-to-r from-pink-500 to-amber-400 border-none text-white font-semibold hover:shadow-glow-gold hover:scale-[1.02] transition-all ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? <span className="loading loading-spinner loading-sm" /> : 'Create Account'}
            </button>
          </form>
          <div className="divider text-gray-500 text-sm my-6">Already have an account?</div>
          <Link to="/login" className="btn btn-outline w-full border-pink-500/50 text-pink-400 hover:bg-pink-500/10 hover:border-pink-400">
            Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}